import { _ } from "../../../../core/polyglot/polyglot.js";
import { select, spacer } from "../../../../core/elements/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import button from "@foretagsplatsen/finsit-design-system/widget-js/elements/button";
import ConsecutiveMonthsSelectionWidget from "../../../../core/widgets/ConsecutiveMonthsSelectionWidget/ConsecutiveMonthsSelectionWidget.js";

/**
 * `PeriodSelectionSectionWidget` renders a section where the user can
 * select the appropriate period for the period report being
 * edited.
 *
 * @param {FiscalYear[]} fiscalYears - List of fiscal years from which
 *   the period can be selected.
 * @param {Period} selectedPeriod - Pre-selection when rendering the
 *   widget for the first time.
 * @param {function} onChange - Callback executed when clicking on the
 *   update button. It takes the selected period as argument.
 */
export default class PeriodSelectionSectionWidget extends Widget {
	constructor({
		fiscalYears = [],
		onChange = () => {},
		selectedPeriod,
	} = {}) {
		super(...arguments);

		this._fiscalYears = [...fiscalYears];

		this._fiscalYears.sort((year1, year2) =>
			year1.endsBefore(year2) ? 1 : -1,
		);

		this._onChange = onChange;

		this._selectedYear = selectedPeriod
			? selectedPeriod.getYear()
			: fiscalYears[0];

		this._consecutiveMonths = selectedPeriod?.isConsecutiveMonths()
			? selectedPeriod
			: this._selectedYear.asLastConsecutiveMonthsWithinActivePeriod();
	}

	renderContentOn(html) {
		html.div(
			{
				class: "PeriodSelectionSectionWidget",
				"data-test": "custom-period-selection",
			},
			spacer(
				{ direction: "horizontal", isBlock: true, align: "right" },
				_("Fiscal year"),
				this._makeFiscalYearSelection(),
				this._makeConsecutiveMonthsSelection(),
				this._makeUpdateButton(),
			),
		);
	}

	_makeFiscalYearSelection() {
		return select({
			items: this._fiscalYears.map((year) => ({
				value: year,
				label: year.getName(),
			})),
			selected: this._selectedYear,
			name: "fiscal-year",
			onChange: this._onYearChange.bind(this),
		});
	}

	_makeConsecutiveMonthsSelection() {
		return new ConsecutiveMonthsSelectionWidget({
			fiscalYear: this._selectedYear,
			consecutiveMonths: this._consecutiveMonths,
			onChange: (period) => (this._consecutiveMonths = period),
		});
	}

	_makeUpdateButton() {
		return button(
			{
				primary: true,
				dataTest: "update",
				onClick: () => this._onChange(this._consecutiveMonths),
			},
			_("Update"),
		);
	}

	_onYearChange(fiscalYear) {
		this._selectedYear = fiscalYear;

		this._consecutiveMonths =
			this._consecutiveMonths?.getEquivalentInFiscalYear(fiscalYear);

		this.update();
	}
}
